import type { NextApiRequest, NextApiResponse } from 'next'
import {
  type CreateUpdateOrderDTO,
  type Language,
  type OrderAddressDTO,
  type OrderShippingDTO
} from 'ecosystem'
import { useLocaleCredentials as setLocaleCredentials } from 'api'
import storefrontApi from 'api/storefront-api'
import { transformOrderItemsToCreateOrderItems } from 'api/utils/orders'
import { carrierFieldsFromSelectedMethod, createLogger } from 'shared-utils'
import { handlerWithLogging } from '../../../logging'
import { type UpdateDefaultShipmentHandlerOpts, type UpdateDefaultShipmentRequest } from './types'

const logger = createLogger('updateDefaultShipmentHandler')

/**
 * POST /api/orders/update-default-shipment — persist the FE-driven delivery
 * selection onto an existing UPFRONT order when the store has no third-party
 * carrier configured (DEFAULT shipping branch).
 *
 * Writes the v1 flat shipping fields (`shippingAddress`, `shippingCost`,
 * `shippingInfo.*`) because the storefront orders client targets the v1
 * endpoint (`PUT /storefront/orders`), which ignores a `shipments[]` array
 * on input — v1 reconstructs `shipments[0]` from the flat fields on save
 * (see `FEATURE_ORDER_PLACEMENT.md §5.2`). The raw FE selection is mirrored
 * onto `shippingInfo.additionalInfo.defaultDetails`, same pattern as
 * `postnordDetails` / `nshiftDetails` are stored by the provision handler,
 * and the chosen method is additionally mirrored onto `carrierService` /
 * `carrierName` (see `carrierFieldsFromSelectedMethod`) so the selection is
 * queryable without unpacking that blob.
 *
 * 1. GET the persisted order by `orderNumber` (full PUT, so we round-trip
 *    everything we don't intend to change).
 * 2. Patch the shipping fields based on `defaultDetails`:
 *    - PICKUP → `shippingType: 'PICKUP'`, the pickup's address goes on
 *      `shippingAddress` + `shippingInfo.pickupAddress`, `pickupLocationId`,
 *      `pickupLocationName`. `shippingCost = 0`.
 *    - DELIVERY → `shippingType: 'DELIVERY'`, and only set `shippingAddress`
 *      when the FE owns it (B2B custom-shipping flow). The payment widget
 *      will fill it in via the push handler when the FE doesn't.
 * 3. PUT.
 *
 * Payment-provider agnostic — per-provider cart updates (Svea cart
 * shipping line, etc.) go through `updateOrder` separately on the same tick.
 */
export const updateDefaultShipmentHandler = handlerWithLogging(
  async (req: NextApiRequest, res: NextApiResponse, opts?: UpdateDefaultShipmentHandlerOpts) => {
    if (req.method !== 'POST') {
      res.status(405).json({ error: 'Method not supported.' })
      return
    }

    const locale = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    setLocaleCredentials(locale)

    try {
      const body: UpdateDefaultShipmentRequest =
        typeof req.body === 'string' ? JSON.parse(req.body) : req.body
      const { orderNumber, defaultDetails, shippingCost } = body

      if (!orderNumber) {
        res.status(400).json({ error: 'orderNumber is required.' })
        return
      }
      if (!defaultDetails) {
        res.status(400).json({ error: 'defaultDetails is required.' })
        return
      }

      const { data: existing, error: getErr } = await storefrontApi.orders.getStorefrontOrder({
        orderNumber
      })
      if (getErr || !existing) {
        res.status(404).json({ error: typeof getErr === 'string' ? getErr : 'order-not-found' })
        return
      }

      const pickup = defaultDetails.selectedPickup
      const pickupAddress: OrderAddressDTO | undefined =
        defaultDetails.isPickupFromStore && pickup?.address
          ? {
              address1: pickup.address.address1 ?? '',
              address2: pickup.address.address2 ?? '',
              country: pickup.address.country ?? '',
              mobilePhone: pickup.address.mobilePhone ?? '',
              postalCode: pickup.address.postalCode ?? '',
              postalRegion: pickup.address.postalRegion ?? '',
              attention: pickup.address.attention ?? '',
              reference: pickup.address.reference ?? ''
            }
          : undefined

      // Canonical `shippingAddress` for the BE: pickup address takes
      // precedence when PICKUP, otherwise the FE-supplied shipping address
      // (only set when the FE owns the address — `showShippingAddress=true`
      // on the consumer, or invoice flow without a payment widget).
      const effectiveShippingAddress =
        pickupAddress ?? defaultDetails.shippingAddress ?? existing.shippingAddress

      const nextShippingInfo: OrderShippingDTO = {
        ...existing.shippingInfo,
        shippingType: defaultDetails.isPickupFromStore ? 'PICKUP' : 'DELIVERY',
        // Mirror the chosen method onto the canonical carrier fields, the same
        // way the invoice flow does — otherwise an UPFRONT order records the
        // choice only inside `additionalInfo`. These are the fields the read
        // paths key on: `ConfirmationPageStd` shows `carrierName` as the
        // shipping method, `ShipmentCard`'s header is `carrierName —
        // carrierService`, and `hasShipmentData` counts them when deciding
        // whether a synthetic shipment is worth rendering at all (it ignores
        // `shippingType` and `address`).
        ...carrierFieldsFromSelectedMethod(defaultDetails.selectedMethod),
        ...(pickup?.id ? { pickupLocationId: pickup.id } : {}),
        ...(pickup?.name ? { pickupLocationName: pickup.name } : {}),
        ...(pickupAddress ? { pickupAddress } : {}),
        additionalInfo: {
          ...existing.shippingInfo?.additionalInfo,
          defaultDetails
        }
      }

      const payload: CreateUpdateOrderDTO = {
        ...existing,
        orderNumber: existing.orderNumber ?? orderNumber,
        ...(opts?.customerId ? { customerId: opts.customerId } : {}),
        items: existing.items ? transformOrderItemsToCreateOrderItems(existing.items) : [],
        ...(shippingCost !== undefined ? { shippingCost } : {}),
        ...(effectiveShippingAddress ? { shippingAddress: effectiveShippingAddress } : {}),
        shippingInfo: nextShippingInfo
      }

      logger.log('BE REQ PAYLOAD', payload)

      const updated = await storefrontApi.orders.updateStorefrontOrder(payload)
      res.status(200).json(updated)
    } catch (e) {
      res.status(400).json({ error: e instanceof Error ? e.message : 'Unknown error' })
    }
  }
)
